import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AppDispatch } from "@/app/store";
import { RootState } from "@/app/rootReducer";
import {
  fetchCampaignDetailsAction,
  clearCampaignDetails,
} from "@/modules/stochastic/features/CampaignManagement/slices/CampaignDetailsSlice";
import { useCreateCampaign } from "./useCreateCampaign";

export function useCloneCampaign() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const hasAppliedFilters = useRef(false);
  const hasPrePopulated = useRef(false);
  const originalPostalCodes = useRef<
    { postalCode: string | number; limit: number }[]
  >([]);

  const { campaignDetails, loading: loadingOriginalCampaign } = useSelector(
    (state: RootState) => state.campaignDetails,
  );

  const createCampaignHook = useCreateCampaign();

  useEffect(() => {
    if (id) {
      // Reset refs when ID changes to allow re-population
      hasPrePopulated.current = false;
      hasAppliedFilters.current = false;
      originalPostalCodes.current = [];

      // Clear previous campaign data before fetching new one
      dispatch(clearCampaignDetails());
      dispatch(fetchCampaignDetailsAction({ campaignId: id }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    // Only populate if campaign details match the requested ID
    if (
      campaignDetails &&
      campaignDetails.id === Number(id) &&
      createCampaignHook.campaignProducts.length > 0 &&
      !hasPrePopulated.current
    ) {
      hasPrePopulated.current = true;
      const selectedProduct = createCampaignHook.campaignProducts.find(
        (p) => p.id === campaignDetails.campaignProduct?.id,
      );
      originalPostalCodes.current = campaignDetails.postalCodeLimits || [];

      createCampaignHook.setFormData((prev) => ({
        ...prev,
        campaignProductId: selectedProduct?.id.toString() ?? "",
        campaignName: `${campaignDetails.name} (Copy)`,
        description: campaignDetails.description || "",
        phoneNumber: campaignDetails.phoneNumber || "",
        licenseNumber: campaignDetails.licenseNumber || "",
        permitNumber: campaignDetails.permitNumber || "",
        startDate: "",
        endDate: "",
        mailingFrequency:
          campaignDetails.mailingSchedule?.mailingFrequency?.value?.toString() ||
          "",
        selectedMailingWeeks:
          campaignDetails.mailingSchedule?.mailingDropWeeks?.map(
            (week) => week.weekNumber,
          ) || [],
        locations: campaignDetails.locations?.map((l) => l.id) || [],
        filterCriteria: {
          audience:
            campaignDetails.filters?.campaignTarget?.value ||
            "include_prospects_only",
          addressType:
            campaignDetails.filters?.addressType?.value ||
            "include_residential_only",
          prospectAge: {
            min:
              campaignDetails.filters?.demographicTargets?.find(
                (t) => t.name === "Prospect Age Min",
              )?.value || "40",
            max:
              campaignDetails.filters?.demographicTargets?.find(
                (t) => t.name === "Prospect Age Max",
              )?.value || "90",
          },
          estimatedIncome:
            campaignDetails.filters?.demographicTargets?.find(
              (t) => t.name === "Estimated Income",
            )?.value || "",
          homeAge:
            campaignDetails.filters?.demographicTargets?.find(
              (t) => t.name === "Home Age",
            )?.value || "5",
          excludeClubMembers:
            campaignDetails.filters?.customerRestrictionCriteria?.some(
              (c) => c.value === "exclude_club_members",
            ) || false,
          excludeLTV:
            campaignDetails.filters?.customerRestrictionCriteria?.some(
              (c) => c.value === "5000",
            ) || false,
          excludeInstallCustomers:
            campaignDetails.filters?.customerRestrictionCriteria?.some(
              (c) => c.value === "exclude_customer_installations",
            ) || false,
        },
        tags: campaignDetails.filters?.tags?.map((t) => t.name).join(",") || "",
        creativeAssetFrontUuid:
          campaignDetails.creativeAssets?.front?.uuid ?? null,
        creativeAssetBackUuid:
          campaignDetails.creativeAssets?.back?.uuid ?? null,
      }));

      const front = campaignDetails.creativeAssets?.front;
      if (front?.uuid && front.publicUrl) {
        createCampaignHook.handleCreativeAssetFrontSelected([
          {
            fileUuid: front.uuid,
            fileUrl: front.publicUrl,
            publicUrl: front.publicUrl,
            name: front.name ?? "",
            originalFilename: front.name ?? "",
          },
        ]);
      }

      const back = campaignDetails.creativeAssets?.back;
      if (back?.uuid && back.publicUrl) {
        createCampaignHook.handleCreativeAssetBackSelected([
          {
            fileUuid: back.uuid,
            fileUrl: back.publicUrl,
            publicUrl: back.publicUrl,
            name: back.name ?? "",
            originalFilename: back.name ?? "",
          },
        ]);
      }

      if (!hasAppliedFilters.current) {
        hasAppliedFilters.current = true;
        setTimeout(() => {
          createCampaignHook.applyFilters();
        }, 100);
      }
    }
  }, [campaignDetails, createCampaignHook.campaignProducts, id]);

  useEffect(() => {
    if (
      createCampaignHook.formData.zipCodes.length > 0 &&
      originalPostalCodes.current.length > 0 &&
      hasAppliedFilters.current
    ) {
      const originalSelections = new Map(
        originalPostalCodes.current.map((pc) => [
          pc.postalCode.toString(),
          pc.limit,
        ]),
      );

      const updatedZipCodes = createCampaignHook.formData.zipCodes.map(
        (zipCode) => {
          const originalLimit = originalSelections.get(zipCode.code);

          if (originalLimit !== undefined) {
            const selectedProspects = Math.min(
              originalLimit,
              zipCode.filteredProspects,
            ).toString();

            return {
              ...zipCode,
              selectedProspects,
            };
          }

          return zipCode;
        },
      );

      createCampaignHook.setFormData((prev) => ({
        ...prev,
        zipCodes: updatedZipCodes,
      }));

      originalPostalCodes.current = [];
    }
  }, [createCampaignHook.formData.zipCodes]);

  return {
    ...createCampaignHook,
    loadingOriginalCampaign,
  };
}
